import { errorCopy } from '@/lib/auth';
import { useAuthStore, useZodForm } from '@bemor/web-kit';
import {
    authCall,
    AuthHead,
    challengeOf,
    CodeStep,
    EnrolStep,
    enterApp,
    ErrorBanner,
    googleHref,
    NewPasswordFields,
    newPasswordSchema,
    StaffAuth,
    useAuthSizes,
    type AcceptAnswer,
    type AuthError,
    type InvitationPreview,
} from '@bemor/web-kit/staff-auth';
import { Button, DateTime, KeyValueList, Skeleton } from '@bemor/web-kit/ui';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

const roleLabel = (role: string) => {
    const s = role.replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
};

type InviteState =
    | { step: 'default' }
    | { step: 'sso' }
    | { step: 'password' }
    | { step: 'totp-enrol'; sessionId: string }
    | { step: 'totp'; sessionId: string }
    | { step: 'done' }
    | { step: 'expired' };

/**
 * `/invite/:token` (design `backoffice/invite`; also `/auth/invitations/accept?token=`, the link Delivery's invitation
 * email carries today). BM-45: staff accept with Google only, as the invited email, then set up the authenticator.
 *
 * - Google: the browser goes to Google carrying the invitation; Identity accepts it on the way back when the Google
 *   account is the invited email on an internal domain, and the callback page runs the authenticator setup.
 * - Password: only for an invitation whose preview says `sign_in: password` (never a staff one); the password with the
 *   rules and its confirmation accepts it, then the authenticator, then "You are in".
 *
 * The invitation is previewed by its token (`GET /auth/invitations/{token}`: email, role, inviter, expiry, state,
 * sign_in) to name the inviter, the role and the email to use; an unknown, used or expired token shows `expired`.
 */
export function InvitePage() {
    const sizes = useAuthSizes();
    const params = useParams();
    const [search] = useSearchParams();
    const token = params.token ?? search.get('token') ?? '';
    const tenantName =
        useAuthStore((s) => s.tenant?.brand.display_name) ?? 'Bemor';
    const [state, setState] = useState<InviteState>(
        token ? { step: 'default' } : { step: 'expired' },
    );
    const [error, setError] = useState<AuthError | null>(null);
    const [busy, setBusy] = useState(false);
    const form = useZodForm(newPasswordSchema, {
        defaultValues: { password: '', confirm: '' },
    });

    const [preview, setPreview] = useState<InvitationPreview | null>(null);
    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        void authCall<InvitationPreview>(
            'GET',
            `/api/auth/invitations/${encodeURIComponent(token)}`,
        ).then((r) => {
            if (cancelled) return;
            if (
                r.ok &&
                r.data &&
                typeof r.data === 'object' &&
                'email' in r.data
            ) {
                setPreview(r.data);
                if (r.data.state && r.data.state !== 'open')
                    setState({ step: 'expired' });
            } else if (
                !r.ok &&
                ['invalid_invitation', 'invitation_expired'].includes(
                    r.error.key,
                )
            )
                setState({ step: 'expired' });
        });
        return () => {
            cancelled = true;
        };
    }, [token]);

    const accept = async (password?: { password: string; confirm: string }) => {
        setBusy(true);
        setError(null);
        const r = await authCall<AcceptAnswer>(
            'POST',
            '/auth/invitations/accept',
            {
                token,
                ...(password
                    ? {
                          password: password.password,
                          password_confirmation: password.confirm,
                      }
                    : {}),
            },
        );
        setBusy(false);
        if (!r.ok) {
            if (r.error.key === 'invalid_invitation')
                setState({ step: 'expired' });
            else if (r.error.key === 'weak_password')
                form.setError('password', { message: errorCopy(r.error) });
            else if (r.error.key === 'password_mismatch')
                form.setError('confirm', { message: errorCopy(r.error) });
            else setError(r.error);
            return null;
        }
        return r.data;
    };

    // BM-45: a staff invitation is accepted by signing in with Google as the invited email; Identity accepts it
    // when Google returns (`GET /auth/google/redirect?invitation=`), never through the password accept route.
    const viaGoogle = () => {
        setState({ step: 'sso' });
        window.location.assign(
            `${googleHref('/')}&invitation=${encodeURIComponent(token)}`,
        );
    };
    // Only a merchant-style invitation (`sign_in: password`) can still be accepted with a password.
    const passwordAllowed = preview?.sign_in === 'password';

    const viaPassword = form.handleSubmit(async (values) => {
        const answer = await accept(values);
        if (!answer) return;
        const challenge = challengeOf(answer);
        if (challenge === 'totp_enrolment')
            setState({ step: 'totp-enrol', sessionId: answer.session_id });
        else if (challenge === 'totp')
            setState({ step: 'totp', sessionId: answer.session_id });
        else setState({ step: 'done' });
    });

    const restart = () => enterApp('/login');

    if (state.step === 'expired')
        return (
            <StaffAuth>
                <div
                    data-testid="invite-expired"
                    className="flex flex-col gap-5"
                >
                    <AuthHead
                        title="This invitation has expired"
                        sub="Invitations work for 7 days and only once. Ask the staff manager who invited you to send a new one."
                    />
                    <Button
                        asChild
                        variant="secondary"
                        size={sizes.button}
                        className="w-full no-underline"
                    >
                        <a href="/login">Go to sign in</a>
                    </Button>
                </div>
            </StaffAuth>
        );

    if (state.step === 'totp-enrol')
        return (
            <StaffAuth>
                <EnrolStep
                    sessionId={state.sessionId}
                    title="Set up your code"
                    sub="Admin accounts need a second step. Scan this with an authenticator app, then enter the code it shows."
                    doneLabel="Continue"
                    onDone={() => setState({ step: 'done' })}
                    onRestart={restart}
                />
            </StaffAuth>
        );

    if (state.step === 'totp')
        return (
            <StaffAuth>
                <CodeStep
                    sessionId={state.sessionId}
                    onDone={() => setState({ step: 'done' })}
                    onRestart={restart}
                />
            </StaffAuth>
        );

    if (state.step === 'done')
        return (
            <StaffAuth>
                <div data-testid="invite-done" className="flex flex-col gap-5">
                    <AuthHead
                        title="You are in"
                        sub={`Your ${tenantName} Admin account is ready. Your roles decide what you see.`}
                    />
                    <Button
                        variant="brand"
                        size={sizes.button}
                        className="w-full"
                        onClick={() => enterApp('/')}
                    >
                        Open Admin
                    </Button>
                </div>
            </StaffAuth>
        );

    if (state.step === 'sso')
        return (
            <StaffAuth>
                <div
                    role="status"
                    aria-busy="true"
                    className="flex flex-col gap-4"
                    data-testid="invite-sso"
                >
                    <AuthHead
                        title="Connecting your Google account"
                        sub={
                            preview
                                ? `Google asks you to choose the account. Use ${preview.email}.`
                                : 'Google asks you to choose the account. Use the invited email.'
                        }
                    />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-5 w-3/5" />
                </div>
            </StaffAuth>
        );

    if (state.step === 'password')
        return (
            <StaffAuth>
                <AuthHead
                    title="Set a password"
                    sub="You sign in with it and a code from your authenticator app."
                />
                {error ? (
                    <ErrorBanner
                        error={error}
                        title="The invitation was not accepted"
                    />
                ) : null}
                <form
                    method="post"
                    onSubmit={viaPassword}
                    className="flex flex-col gap-4"
                    data-testid="invite-password-form"
                    noValidate
                >
                    <NewPasswordFields form={form} />
                    <Button
                        type="submit"
                        variant="brand"
                        size={sizes.button}
                        className="w-full"
                        loading={busy}
                    >
                        Save and continue
                    </Button>
                </form>
                <button
                    type="button"
                    className="text-fg-2 self-start text-sm underline-offset-4 hover:underline"
                    onClick={() => setState({ step: 'default' })}
                >
                    Use Google instead
                </button>
            </StaffAuth>
        );

    return (
        <StaffAuth>
            <div data-testid="invite-default" className="flex flex-col gap-5">
                <AuthHead
                    title={`Join ${tenantName} Admin`}
                    sub={
                        preview ? (
                            <>
                                {preview.inviter_name ?? 'A staff manager'}{' '}
                                invited you as {roleLabel(preview.role)}. The
                                invitation expires{' '}
                                <DateTime
                                    value={preview.expires_at}
                                    format="date"
                                />
                                .
                            </>
                        ) : (
                            'You were invited to work in Admin. Sign in with your company Google account; a code from an authenticator app follows.'
                        )
                    }
                />
                {preview ? (
                    <KeyValueList
                        layout="stacked"
                        items={[{ label: 'Email', value: preview.email }]}
                    />
                ) : null}
                {error ? (
                    <ErrorBanner
                        error={error}
                        title="The invitation was not accepted"
                    />
                ) : null}
                <Button
                    variant="brand"
                    size={sizes.button}
                    className="w-full"
                    loading={busy}
                    onClick={viaGoogle}
                    data-testid="invite-google"
                >
                    {preview
                        ? `Continue with Google as ${preview.email}`
                        : 'Continue with Google'}
                </Button>
                {passwordAllowed ? (
                    <Button
                        variant="secondary"
                        size={sizes.button}
                        className="w-full"
                        disabled={busy}
                        onClick={() => setState({ step: 'password' })}
                    >
                        Set a password instead
                    </Button>
                ) : null}
            </div>
        </StaffAuth>
    );
}
